/**
 * React Slick slider configuration presets
 * 
 * Pre-configured slider settings for different use cases throughout the application.
 * These settings are optimized for responsive design and user experience.
 */

/**
 * Slider settings with navigation dots enabled
 * 
 * Configuration for sliders that show navigation dots at the bottom.
 * Includes responsive breakpoints for tablet and mobile views.
 * 
 * Features:
 * - Infinite loop
 * - Autoplay enabled
 * - Navigation dots visible
 * - Responsive: 2 slides on tablet, 1 on mobile
 * 
 * @example
 * ```tsx
 * import Slider from 'react-slick'
 * import { sliderSettingsWithDot } from '@/utils/sliderSettings'
 * 
 * <Slider {...sliderSettingsWithDot}>
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 * </Slider>
 * ```
 */
export const sliderSettingsWithDot = {
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  dots: true,
  arrows: false,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        dots: true,
        arrows: false,
      },
    },
    {
      breakpoint: 640,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        dots: true,
        arrows: false,
      },
    },
  ],
}

/**
 * Slider settings without navigation dots
 * 
 * Configuration for sliders without navigation dots.
 * Includes responsive breakpoints for tablet and mobile views.
 * 
 * Features:
 * - Infinite loop
 * - Autoplay enabled
 * - No navigation dots
 * - Responsive: 2 slides on tablet, 1 on mobile
 * 
 * @example
 * ```tsx
 * import Slider from 'react-slick'
 * import { sliderSettingsWithoutDot } from '@/utils/sliderSettings'
 * 
 * <Slider {...sliderSettingsWithoutDot}>
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 * </Slider>
 * ```
 */
export const sliderSettingsWithoutDot = {
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  dots: false,
  arrows: false,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        dots: false,
        arrows: false,
      },
    },
    {
      breakpoint: 640,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        dots: false,
        arrows: false,
      },
    },
  ],
}

/**
 * Review slider settings without dots
 * 
 * Configuration optimized for review/testimonial sliders.
 * Features adaptive height to accommodate varying content lengths.
 * 
 * Features:
 * - No infinite loop (stops at end)
 * - Adaptive height (adjusts to content)
 * - No navigation dots
 * - Single slide view
 * 
 * @example
 * ```tsx
 * import Slider from 'react-slick'
 * import { reviewSliderSettingsWithoutDot } from '@/utils/sliderSettings'
 * 
 * <Slider {...reviewSliderSettingsWithoutDot}>
 *   {reviews.map(review => <ReviewCard key={review.id} review={review} />)}
 * </Slider>
 * ```
 */
export const reviewSliderSettingsWithoutDot = {
  infinite: false,
  slidesToShow: 1,
  dots: false,
  arrows: false,
  adaptiveHeight: true,
}

/**
 * Review slider settings with dots
 * 
 * Configuration optimized for review/testimonial sliders with navigation dots.
 * Features slower autoplay speed and adaptive height for better UX.
 * 
 * Features:
 * - Infinite loop
 * - Slower autoplay (6 seconds)
 * - Navigation dots visible
 * - Adaptive height (adjusts to content)
 * - Single slide view
 * 
 * @example
 * ```tsx
 * import Slider from 'react-slick'
 * import { reviewSliderSettingsWithDot } from '@/utils/sliderSettings'
 * 
 * <Slider {...reviewSliderSettingsWithDot}>
 *   {reviews.map(review => <ReviewCard key={review.id} review={review} />)}
 * </Slider>
 * ```
 */
export const reviewSliderSettingsWithDot = {
  infinite: true,
  speed: 1500,
  autoplaySpeed: 6000,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  dots: true,
  arrows: false,
  adaptiveHeight: true,
}
